import { Server, useForceUpdate } from '@khanacademy/wonder-blocks-core';
import { KindError, clone, entries } from '@khanacademy/wonder-stuff-core';
import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { useContext, useRef, useMemo, useCallback } from 'react';

let FetchPolicy = function (FetchPolicy) {
  FetchPolicy["CacheBeforeNetwork"] = "CacheBeforeNetwork";
  FetchPolicy["CacheAndNetwork"] = "CacheAndNetwork";
  FetchPolicy["CacheOnly"] = "CacheOnly";
  FetchPolicy["NetworkOnly"] = "NetworkOnly";
  return FetchPolicy;
}({});

const DataErrors = Object.freeze({
  Unknown: "Unknown",
  Internal: "Internal",
  InvalidInput: "InvalidInput",
  Network: "Network",
  NotAllowed: "NotAllowed",
  Parse: "Parse",
  Hydrated: "Hydrated"
});
class DataError extends KindError {
  constructor(message, kind, {
    metadata,
    cause
  } = {}) {
    super(message, kind, {
      metadata,
      cause,
      name: "Data"
    });
  }
}

class ScopedInMemoryCache {
  constructor(initialCache = {}) {
    this._cache = void 0;
    this._cache = initialCache;
  }
  get inUse() {
    return Object.keys(this._cache).length > 0;
  }
  set(scope, id, value) {
    var _this$_cache$scope;
    if (!id || typeof id !== "string") {
      throw new DataError("id must be non-empty string", DataErrors.InvalidInput);
    }
    if (!scope || typeof scope !== "string") {
      throw new DataError("scope must be non-empty string", DataErrors.InvalidInput);
    }
    if (typeof value === "function") {
      throw new DataError("value must be a non-function value", DataErrors.InvalidInput);
    }
    this._cache[scope] = (_this$_cache$scope = this._cache[scope]) != null ? _this$_cache$scope : {};
    this._cache[scope][id] = value;
  }
  get(scope, id) {
    var _this$_cache$scope$id, _this$_cache$scope2;
    return (_this$_cache$scope$id = (_this$_cache$scope2 = this._cache[scope]) == null ? void 0 : _this$_cache$scope2[id]) != null ? _this$_cache$scope$id : null;
  }
  purge(scope, id) {
    var _this$_cache$scope3;
    if (!((_this$_cache$scope3 = this._cache[scope]) != null && _this$_cache$scope3[id])) {
      return;
    }
    delete this._cache[scope][id];
    if (Object.keys(this._cache[scope]).length === 0) {
      delete this._cache[scope];
    }
  }
  purgeScope(scope, predicate) {
    if (!this._cache[scope]) {
      return;
    }
    if (predicate == null) {
      delete this._cache[scope];
      return;
    }
    for (const key of Object.keys(this._cache[scope])) {
      if (predicate(key, this._cache[scope][key])) {
        delete this._cache[scope][key];
      }
    }
    if (Object.keys(this._cache[scope]).length === 0) {
      delete this._cache[scope];
    }
  }
  purgeAll(predicate) {
    if (predicate == null) {
      this._cache = {};
      return;
    }
    for (const scope of Object.keys(this._cache)) {
      this.purgeScope(scope, (id, value) => predicate(scope, id, value));
    }
  }
}

class SerializableInMemoryCache extends ScopedInMemoryCache {
  constructor(initialCache = {}) {
    try {
      super(clone(initialCache));
    } catch (e) {
      throw new DataError(`An error occurred trying to initialize from a response cache snapshot: ${e}`, DataErrors.InvalidInput);
    }
  }
  set(scope, id, value) {
    super.set(scope, id, Object.freeze(clone(value)));
  }
  clone() {
    try {
      return clone(this._cache);
    } catch (e) {
      throw new DataError("An error occurred while trying to clone the cache", DataErrors.Internal, {
        cause: e
      });
    }
  }
}

const DefaultScope$2 = "default";
let _default$2;
class SsrCache {
  static get Default() {
    if (!_default$2) {
      _default$2 = new SsrCache();
    }
    return _default$2;
  }
  constructor(hydrationCache = null, ssrOnlyCache = null) {
    this._hydrationCache = void 0;
    this._ssrOnlyCache = void 0;
    this.initialize = source => {
      if (this._hydrationCache.inUse) {
        throw new Error("Cannot initialize data response cache more than once");
      }
      this._hydrationCache = new SerializableInMemoryCache({
        [DefaultScope$2]: source
      });
    };
    this.cacheData = (id, data, hydrate) => this._setCachedResponse(id, {
      data
    }, hydrate);
    this.cacheError = (id, error, hydrate) => {
      const errorMessage = typeof error === "string" ? error : error.message;
      return this._setCachedResponse(id, {
        error: errorMessage
      }, hydrate);
    };
    this.getEntry = id => {
      const ssrEntry = Server.isServerSide() ? this._ssrOnlyCache.get(DefaultScope$2, id) : null;
      const internalEntry = ssrEntry != null ? ssrEntry : this._hydrationCache.get(DefaultScope$2, id);
      if (!Server.isServerSide() && internalEntry != null) {
        this._hydrationCache.purge(DefaultScope$2, id);
      }
      return internalEntry;
    };
    this.purgeData = predicate => {
      const realPredicate = predicate ? (_, key, cachedEntry) => predicate(key, cachedEntry) : undefined;
      this._hydrationCache.purgeAll(realPredicate);
      this._ssrOnlyCache.purgeAll(realPredicate);
    };
    this.cloneHydratableData = () => {
      var _ref;
      const cache = this._hydrationCache.clone();
      return (_ref = cache[DefaultScope$2]) != null ? _ref : {};
    };
    this._ssrOnlyCache = ssrOnlyCache || new SerializableInMemoryCache();
    this._hydrationCache = hydrationCache || new SerializableInMemoryCache();
  }
  _setCachedResponse(id, entry, hydrate) {
    const frozenEntry = Object.freeze(entry);
    if (Server.isServerSide()) {
      if (hydrate) {
        this._hydrationCache.set(DefaultScope$2, id, frozenEntry);
      } else {
        this._ssrOnlyCache.set(DefaultScope$2, id, frozenEntry);
      }
    }
    return frozenEntry;
  }
}

const initializeHydrationCache = source => SsrCache.Default.initialize(source);
const purgeHydrationCache = predicate => SsrCache.Default.purgeData(predicate);

let _default$1;
class RequestFulfillment {
  constructor() {
    this._requests = {};
    this.fulfill = (id, {
      handler,
      hydrate: _hydrate = true
    }) => {
      const inflight = this._requests[id];
      if (inflight) {
        return inflight;
      }
      const request = handler().then(data => ({
        status: "success",
        data
      })).catch(error => {
        const actualError = typeof error === "string" ? new DataError("Request failed", DataErrors.Unknown, {
          metadata: {
            unexpectedError: error
          }
        }) : error;
        if (actualError.name === "AbortError") {
          return {
            status: "aborted"
          };
        }
        return {
          status: "error",
          error: actualError
        };
      }).finally(() => {
        delete this._requests[id];
      });
      this._requests[id] = request;
      return request;
    };
    this.abort = id => {
      delete this._requests[id];
    };
    this.abortAll = () => {
      Object.keys(this._requests).forEach(id => this.abort(id));
    };
  }
  static get Default() {
    if (!_default$1) {
      _default$1 = new RequestFulfillment();
    }
    return _default$1;
  }
}

const TrackerContext = React.createContext(null);
TrackerContext.displayName = "TrackerContext";
let _default;
class RequestTracker {
  static get Default() {
    if (!_default) {
      _default = new RequestTracker();
    }
    return _default;
  }
  constructor(responseCache) {
    this._trackedRequests = {};
    this._responseCache = void 0;
    this._requestFulfillment = void 0;
    this.trackDataRequest = (id, handler, hydrate) => {
      if (this._trackedRequests[id] == null) {
        this._trackedRequests[id] = {
          handler,
          hydrate
        };
      }
    };
    this.reset = () => {
      this._trackedRequests = {};
    };
    this.fulfillTrackedRequests = () => {
      const promises = [];
      const {
        cacheData,
        cacheError
      } = this._responseCache;
      for (const requestKey of Object.keys(this._trackedRequests)) {
        const options = this._trackedRequests[requestKey];
        try {
          promises.push(this._requestFulfillment.fulfill(requestKey, _extends({}, options)).then(result => {
            switch (result.status) {
              case "success":
                cacheData(requestKey, result.data, options.hydrate);
                break;
              case "error":
                cacheError(requestKey, result.error, options.hydrate);
                break;
            }
            return;
          }));
        } catch (e) {
          promises.push(Promise.resolve(cacheError(requestKey, e, options.hydrate)));
        }
      }
      this.reset();
      return Promise.all(promises).then(() => this._responseCache.cloneHydratableData());
    };
    this._responseCache = responseCache || SsrCache.Default;
    this._requestFulfillment = new RequestFulfillment();
  }
  get hasUnfulfilledRequests() {
    return Object.keys(this._trackedRequests).length > 0;
  }
}

const SSRCheck = () => {
  if (Server.isServerSide()) {
    return null;
  }
  if (process.env.NODE_ENV === "production") {
    return new DataError("No CSR tracking", DataErrors.NotAllowed);
  } else {
    return new DataError("Data requests are not tracked for fulfillment when when client-side", DataErrors.NotAllowed);
  }
};
const fetchTrackedRequests = () => {
  const ssrCheck = SSRCheck();
  if (ssrCheck != null) {
    return Promise.reject(ssrCheck);
  }
  return RequestTracker.Default.fulfillTrackedRequests();
};
const hasTrackedRequestsToBeFetched = () => {
  const ssrCheck = SSRCheck();
  if (ssrCheck != null) {
    throw ssrCheck;
  }
  return RequestTracker.Default.hasUnfulfilledRequests;
};
const abortInflightRequests = () => {
  RequestFulfillment.Default.abortAll();
};

const cache$1 = new ScopedInMemoryCache();
const SharedCache = cache$1;
const useSharedCache = (id, scope, initialValue) => {
  if (!id || typeof id !== "string") {
    throw new DataError("id must be a non-empty string", DataErrors.InvalidInput);
  }
  if (!scope || typeof scope !== "string") {
    throw new DataError("scope must be a non-empty string", DataErrors.InvalidInput);
  }
  const cacheValue = React.useCallback(value => value == null ? cache$1.purge(scope, id) : cache$1.set(scope, id, value), [id, scope]);
  let currentValue = cache$1.get(scope, id);
  if (currentValue == null && initialValue !== undefined) {
    const value = typeof initialValue === "function" ? initialValue() : initialValue;
    if (value != null) {
      cacheValue(value);
      currentValue = value;
    }
  }
  return [currentValue, cacheValue];
};

const purgeCaches = () => {
  SharedCache.purgeAll();
  purgeHydrationCache();
};

class TrackData extends React.Component {
  render() {
    if (!Server.isServerSide()) {
      throw new Error("This component is not for use during client-side rendering");
    }
    return React.createElement(TrackerContext.Provider, {
      value: RequestTracker.Default.trackDataRequest
    }, this.props.children);
  }
}

const loadingStatus = Object.freeze({
  status: "loading"
});
const noDataStatus = Object.freeze({
  status: "no-data"
});
const abortedStatus = Object.freeze({
  status: "aborted"
});
const Status = Object.freeze({
  loading: () => loadingStatus,
  noData: () => noDataStatus,
  aborted: () => abortedStatus,
  success: data => ({
    status: "success",
    data
  }),
  error: error => ({
    status: "error",
    error
  })
});

const resultFromCachedResponse = cacheEntry => {
  if (cacheEntry == null) {
    return null;
  }
  const {
    data,
    error
  } = cacheEntry;
  if (error != null) {
    return Status.error(new DataError(error, DataErrors.Hydrated));
  }
  if (data != null) {
    return Status.success(data);
  }
  return Status.aborted();
};

const InterceptContext = React.createContext([]);
InterceptContext.displayName = "InterceptContext";

const useRequestInterception = (requestId, handler) => {
  const interceptors = React.useContext(InterceptContext);
  const interceptedHandler = React.useCallback(() => {
    const interceptResponse = interceptors.reduceRight((prev, interceptor) => {
      if (prev != null) {
        return prev;
      }
      return interceptor(requestId);
    }, null);
    return interceptResponse != null ? interceptResponse : handler();
  }, [handler, interceptors, requestId]);
  return interceptedHandler;
};

const useServerEffect = (requestId, handler, options = {}) => {
  const {
    hydrate = true,
    skip = false
  } = options;
  const interceptedHandler = useRequestInterception(requestId, handler);
  const cachedResult = SsrCache.Default.getEntry(requestId);
  const maybeTrack = useContext(TrackerContext);
  if (!skip && cachedResult == null && Server.isServerSide()) {
    maybeTrack == null || maybeTrack(requestId, interceptedHandler, hydrate);
  }
  return cachedResult == null ? null : resultFromCachedResponse(cachedResult);
};

const DefaultScope$1 = "useCachedEffect";
const useCachedEffect = (requestId, handler, options = {}) => {
  var _ref;
  const {
    fetchPolicy = FetchPolicy.CacheBeforeNetwork,
    skip: hardSkip = false,
    retainResultOnChange = false,
    onResultChanged,
    scope = DefaultScope$1
  } = options;
  const interceptedHandler = useRequestInterception(requestId, handler);
  const [mostRecentResult, setMostRecentResult] = useSharedCache(requestId, scope);
  const forceUpdate = useForceUpdate();
  const networkResultRef = React.useRef();
  const currentRequestRef = React.useRef();
  const fetchRequest = React.useMemo(() => {
    var _currentRequestRef$cu;
    (_currentRequestRef$cu = currentRequestRef.current) == null || _currentRequestRef$cu.cancel();
    currentRequestRef.current = null;
    networkResultRef.current = null;
    const fetchFn = () => {
      var _currentRequestRef$cu2, _currentRequestRef$cu3;
      if (fetchPolicy === FetchPolicy.CacheOnly) {
        throw new DataError("Cannot fetch with CacheOnly policy", DataErrors.NotAllowed);
      }
      const request = RequestFulfillment.Default.fulfill(`${requestId}|${scope}`, {
        handler: interceptedHandler
      });
      if (request === ((_currentRequestRef$cu2 = currentRequestRef.current) == null ? void 0 : _currentRequestRef$cu2.request)) {
        return;
      }
      networkResultRef.current = null;
      (_currentRequestRef$cu3 = currentRequestRef.current) == null || _currentRequestRef$cu3.cancel();
      let cancel = false;
      request.then(result => {
        currentRequestRef.current = null;
        if (cancel) {
          return;
        }
        setMostRecentResult(result);
        networkResultRef.current = result;
        if (onResultChanged != null) {
          onResultChanged(result);
        } else {
          forceUpdate();
        }
        return;
      });
      currentRequestRef.current = {
        requestId,
        request,
        cancel() {
          cancel = true;
          RequestFulfillment.Default.abort(requestId);
        }
      };
    };
    return fetchFn;
  }, [requestId, onResultChanged, forceUpdate, setMostRecentResult, fetchPolicy]);
  const shouldFetch = React.useMemo(() => {
    if (hardSkip) {
      return false;
    }
    switch (fetchPolicy) {
      case FetchPolicy.CacheOnly:
        return false;
      case FetchPolicy.CacheBeforeNetwork:
        return mostRecentResult == null;
      case FetchPolicy.CacheAndNetwork:
      case FetchPolicy.NetworkOnly:
        return networkResultRef.current == null;
    }
  }, [mostRecentResult, fetchPolicy, hardSkip]);
  React.useEffect(() => {
    if (!shouldFetch) {
      return;
    }
    fetchRequest();
    return () => {
      var _currentRequestRef$cu4;
      (_currentRequestRef$cu4 = currentRequestRef.current) == null || _currentRequestRef$cu4.cancel();
      currentRequestRef.current = null;
    };
  }, [shouldFetch, fetchRequest]);
  const lastResultAgnosticOfIdRef = React.useRef(Status.noData());
  const loadingResult = retainResultOnChange ? lastResultAgnosticOfIdRef.current : shouldFetch ? Status.loading() : Status.noData();
  const result = (_ref = fetchPolicy === FetchPolicy.NetworkOnly ? networkResultRef.current : mostRecentResult) != null ? _ref : loadingResult;
  lastResultAgnosticOfIdRef.current = result;
  return [result, fetchRequest];
};

let WhenClientSide = function (WhenClientSide) {
  WhenClientSide["DoNotHydrate"] = "DoNotHydrate";
  WhenClientSide["ExecuteWhenNoResult"] = "ExecuteWhenNoResult";
  WhenClientSide["ExecuteWhenNoSuccessResult"] = "ExecuteWhenNoSuccessResult";
  WhenClientSide["AlwaysExecute"] = "AlwaysExecute";
  return WhenClientSide;
}({});
const DefaultScope = "useHydratableEffect";
const useHydratableEffect = (requestId, handler, options = {}) => {
  const {
    clientBehavior = WhenClientSide.ExecuteWhenNoSuccessResult,
    skip = false,
    retainResultOnChange = false,
    onResultChanged,
    scope = DefaultScope
  } = options;
  const serverResult = useServerEffect(requestId, handler, {
    hydrate: clientBehavior !== WhenClientSide.DoNotHydrate,
    skip
  });
  const getDefaultCacheValue = React.useCallback(() => {
    switch (clientBehavior) {
      case WhenClientSide.DoNotHydrate:
      case WhenClientSide.AlwaysExecute:
        return null;
      case WhenClientSide.ExecuteWhenNoResult:
        return serverResult;
      case WhenClientSide.ExecuteWhenNoSuccessResult:
        if ((serverResult == null ? void 0 : serverResult.status) === "success") {
          return serverResult;
        }
        return null;
    }
  }, [serverResult]);
  useSharedCache(requestId, scope, getDefaultCacheValue);
  const [clientResult] = useCachedEffect(requestId, handler, {
    skip,
    onResultChanged,
    retainResultOnChange,
    scope,
    fetchPolicy: FetchPolicy.CacheBeforeNetwork
  });
  return serverResult != null ? serverResult : clientResult;
};

const Data = ({
  requestId,
  handler,
  children,
  retainResultOnChange: _retainResultOnChange = false,
  clientBehavior: _clientBehavior = WhenClientSide.ExecuteWhenNoSuccessResult
}) => {
  const result = useHydratableEffect(requestId, handler, {
    retainResultOnChange: _retainResultOnChange,
    clientBehavior: _clientBehavior
  });
  return children(result);
};

const InterceptRequests = ({
  interceptor,
  children
}) => {
  const interceptors = React.useContext(InterceptContext);
  const updatedInterceptors = React.useMemo(() => [...interceptors, interceptor], [interceptors, interceptor]);
  return React.createElement(InterceptContext.Provider, {
    value: updatedInterceptors
  }, children);
};

const toString = value => {
  var _JSON$stringify;
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && value != null) {
    if (value instanceof Date) {
      return value.toISOString();
    } else if (typeof value.toString === "function") {
      return value.toString();
    }
  }
  return (_JSON$stringify = JSON.stringify(value)) != null ? _JSON$stringify : "";
};
const toStringifiedVariables = (acc, key, value) => {
  if (typeof value === "object" && value !== null) {
    const subValues = entries(value);
    if (subValues.length !== 0) {
      return subValues.reduce((innerAcc, [i, v]) => {
        const subKey = `${key}.${i}`;
        return toStringifiedVariables(innerAcc, subKey, v);
      }, acc);
    }
  }
  acc[key] = toString(value);
  return acc;
};
const getGqlRequestId = (operation, variables, context) => {
  const parts = [];
  const sortableContext = new URLSearchParams(context);
  sortableContext.sort();
  parts.push(sortableContext.toString());
  parts.push(operation.id);
  if (variables != null) {
    const stringifiedVariables = Object.keys(variables).reduce((acc, key) => {
      const value = variables[key];
      return toStringifiedVariables(acc, key, value);
    }, {});
    const sortableVariables = new URLSearchParams(stringifiedVariables);
    sortableVariables.sort();
    parts.push(sortableVariables.toString());
  }
  return parts.join("|");
};

const GqlErrors = Object.freeze({
  Internal: "Internal",
  BadResponse: "BadResponse",
  ErrorResult: "ErrorResult"
});
class GqlError extends KindError {
  constructor(message, kind, {
    metadata,
    cause
  } = {}) {
    super(message, kind, {
      metadata,
      cause,
      name: "Gql"
    });
  }
}

const getGqlDataFromResponse = async response => {
  const bodyText = await response.text();
  let result;
  try {
    result = JSON.parse(bodyText);
  } catch (e) {
    throw new DataError("Failed to parse response", DataErrors.Parse, {
      metadata: {
        statusCode: response.status,
        bodyText
      },
      cause: e
    });
  }
  if (response.status >= 300) {
    throw new DataError("Response unsuccessful", DataErrors.Network, {
      metadata: {
        statusCode: response.status,
        result
      }
    });
  }
  if (!Object.prototype.hasOwnProperty.call(result, "data") && !Object.prototype.hasOwnProperty.call(result, "errors")) {
    throw new GqlError("Server response missing", GqlErrors.BadResponse, {
      metadata: {
        statusCode: response.status,
        result
      }
    });
  }
  if (result.errors != null && Array.isArray(result.errors) && result.errors.length > 0) {
    throw new GqlError("GraphQL errors", GqlErrors.ErrorResult, {
      metadata: {
        statusCode: response.status,
        result
      }
    });
  }
  return result.data;
};

const DocumentTypes = Object.freeze({
  query: "query",
  mutation: "mutation"
});
const cache = new Map();
function graphQLDocumentNodeParser(document) {
  var _definition$name;
  const cached = cache.get(document);
  if (cached) {
    return cached;
  }
  if (!(document != null && document.kind)) {
    if (process.env.NODE_ENV === "production") {
      throw new DataError("Bad DocumentNode", DataErrors.InvalidInput);
    } else {
      throw new DataError(`Argument of ${JSON.stringify(document)} passed to parser was not a valid GraphQL ` + `DocumentNode. You may need to use 'graphql-tag' or another method ` + `to convert your operation into a document`, DataErrors.InvalidInput);
    }
  }
  const fragments = document.definitions.filter(x => x.kind === "FragmentDefinition");
  const queries = document.definitions.filter(x => x.kind === "OperationDefinition" && x.operation === "query");
  const mutations = document.definitions.filter(x => x.kind === "OperationDefinition" && x.operation === "mutation");
  const subscriptions = document.definitions.filter(x => x.kind === "OperationDefinition" && x.operation === "subscription");
  if (fragments.length && !queries.length && !mutations.length) {
    if (process.env.NODE_ENV === "production") {
      throw new DataError("Fragment only", DataErrors.InvalidInput);
    } else {
      throw new DataError(`Passing only a fragment to 'graphql' is not supported. ` + `You must include a query or mutation as well`, DataErrors.InvalidInput);
    }
  }
  if (subscriptions.length) {
    if (process.env.NODE_ENV === "production") {
      throw new DataError("No subscriptions", DataErrors.InvalidInput);
    } else {
      throw new DataError(`We do not support subscriptions. ` + `${JSON.stringify(document)} had ${subscriptions.length} subscriptions`, DataErrors.InvalidInput);
    }
  }
  if (queries.length + mutations.length > 1) {
    if (process.env.NODE_ENV === "production") {
      throw new DataError("Too many ops", DataErrors.InvalidInput);
    } else {
      throw new DataError(`We only support one query or mutation per component. ` + `${JSON.stringify(document)} had ${queries.length} queries and ` + `${mutations.length} mutations. `, DataErrors.InvalidInput);
    }
  }
  const type = queries.length ? DocumentTypes.query : DocumentTypes.mutation;
  const definitions = queries.length ? queries : mutations;
  const definition = definitions[0];
  const variables = definition.variableDefinitions || [];
  const name = ((_definition$name = definition.name) == null ? void 0 : _definition$name.kind) === "Name" ? definition.name.value : "data";
  const payload = {
    name,
    type,
    variables
  };
  cache.set(document, payload);
  return payload;
}

const toGqlOperation = documentNode => {
  const definition = graphQLDocumentNodeParser(documentNode);
  const wbDataOperation = {
    id: definition.name,
    type: definition.type
  };
  return wbDataOperation;
};

const GqlRouterContext = React.createContext(null);
GqlRouterContext.displayName = "GqlRouterContext";

const GqlRouter = ({
  defaultContext: thisDefaultContext,
  fetch: thisFetch,
  children
}) => {
  const configuration = React.useMemo(() => ({
    fetch: thisFetch,
    defaultContext: thisDefaultContext
  }), [thisDefaultContext, thisFetch]);
  return React.createElement(GqlRouterContext.Provider, {
    value: configuration
  }, children);
};

const mergeGqlContext = (defaultContext, overrides) => {
  return Object.keys(overrides).reduce((acc, key) => {
    if (overrides[key] !== undefined) {
      if (overrides[key] === null) {
        delete acc[key];
      } else {
        acc[key] = overrides[key];
      }
    }
    return acc;
  }, _extends({}, defaultContext));
};

const useGqlRouterContext = (contextOverrides = {}) => {
  const gqlRouterContext = useContext(GqlRouterContext);
  if (gqlRouterContext == null) {
    throw new GqlError("No GqlRouter", GqlErrors.Internal);
  }
  const {
    fetch,
    defaultContext
  } = gqlRouterContext;
  const contextRef = useRef(defaultContext);
  const mergedContext = mergeGqlContext(defaultContext, contextOverrides);
  const refKeys = Object.keys(contextRef.current);
  const mergedKeys = Object.keys(mergedContext);
  const shouldWeUpdateRef = refKeys.length !== mergedKeys.length || mergedKeys.every(key => contextRef.current[key] !== mergedContext[key]);
  if (shouldWeUpdateRef) {
    contextRef.current = mergedContext;
  }
  const finalContext = contextRef.current;
  const finalRouterContext = useMemo(() => ({
    fetch,
    defaultContext: finalContext
  }), [fetch, finalContext]);
  return finalRouterContext;
};

const useGql = (context = {}) => {
  const gqlRouterContext = useGqlRouterContext(context);
  const gqlFetch = useCallback((operation, options = Object.freeze({})) => {
    const {
      fetch,
      defaultContext
    } = gqlRouterContext;
    const {
      variables,
      context = {}
    } = options;
    const finalContext = mergeGqlContext(defaultContext, context);
    return fetch(operation, variables, finalContext).then(response => getGqlDataFromResponse(response));
  }, [gqlRouterContext]);
  return gqlFetch;
};

export { Data, DataError, DataErrors, FetchPolicy, GqlError, GqlErrors, GqlRouter, InterceptRequests, ScopedInMemoryCache, SerializableInMemoryCache, SharedCache, Status, TrackData, WhenClientSide, abortInflightRequests, fetchTrackedRequests, getGqlDataFromResponse, getGqlRequestId, graphQLDocumentNodeParser, hasTrackedRequestsToBeFetched, initializeHydrationCache, purgeCaches, purgeHydrationCache, toGqlOperation, useCachedEffect, useGql, useHydratableEffect, useServerEffect, useSharedCache };
